/**
 * The history windows of a handful of items, read once per item revision:
 * one fetch per item (its newest page, newest first), redone when the item
 * changes (its updated_at moves, which the shell's 30s list poll picks up).
 * Until a fetch lands, or when it fails, the item has no entry and nothing
 * may be read from it.
 *
 * Used by the shell for the items sent away (did the hand-off arrive?) and
 * for the items waiting on someone (did that person do something on it?).
 */
import { useEffect, useState } from 'react';
import {
  getThreadHistory,
  type DoorThreadEventRead,
  type DoorThreadRead,
} from '../../services/qcpInvestigations';
import type { TimelineWindow } from './deliveryView';

export type ThreadTimelines = Record<string, TimelineWindow<DoorThreadEventRead>>;

export function useThreadTimelines(
  threads: ReadonlyArray<Pick<DoorThreadRead, 'id' | 'updated_at'>>,
): ThreadTimelines {
  const [timelines, setTimelines] = useState<ThreadTimelines>({});
  const key = threads.map((t) => `${t.id}@${t.updated_at}`).join('|');
  useEffect(() => {
    let cancelled = false;
    for (const t of threads) {
      getThreadHistory(t.id)
        .then((page) => {
          if (!cancelled) setTimelines((prev) => ({ ...prev, [t.id]: page }));
        })
        .catch(() => {
          /* no history: nothing is read from it */
        });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `key` stands for the threads' ids and revisions
  }, [key]);
  return timelines;
}
